import { Badge } from 'antd'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import Routing, { MenuItem, SideMenu } from '../../components/reusable/routing'

import { useInventory, useReports, useLatestPathname, permissions } from '../../modules.js'

import Dispensing from './Dispensing.js'
import Consultation from '../../components/pages/Consultation.js'
import Outreach from './Outreach.js'

let menu = [
    {
        path: 'consultation',
        title: 'Consultation',
        component: () => (
            <Consultation
                specialty="Optical"
                requestDepartment="Optical"
                notificationDestination="Optical"
                path="/app/optical/consultation"
            />
        )
    },
    {
        path: 'dispensing',
        title: 'Dispensing',
        component: Dispensing
    }
]

const storeName = "Optical"

const Optical = () => {
    const history = useHistory()
    const { user } = useSelector(state => state.application)
    const pathname = useLatestPathname()

    const [Reports, reports] = useReports("optical")

    const [Inventory, store, requests] = useInventory(storeName)

    // Eye-clinic clinical/dispensing/inventory/reports items are gated behind the `eye-clinic`
    // station permission. Existing Eye Clinic Staff roles hold `*` on optical and are
    // unaffected; a CHW (outreach-screening only) sees none of these until the Outreach menu
    // is added in Phase 2. See docs/planning/EYE_CLINIC_OUTREACH_SCOPE.md §3.2.
    const canUseEyeClinic = permissions(user, 'optical', 'eye-clinic')
    const canScreenOutreach = permissions(user, 'optical', 'outreach-screening') || permissions(user, 'optical', 'outreach-manage')

    useEffect(() => {
        if (canUseEyeClinic && (user.stores.some(st => st.name === storeName) || user?.issuperuser))
            menu[2] = {
                path: 'inventory',
                title: props =>
                    <Badge className='w-12' count={ requests }>
                        <MenuItem condition={ props?.condition }>Inventory</MenuItem>
                    </Badge>,
                component: ({ path }) => <Inventory requireShift path={ path } storeName={ storeName } />
            }

        if (canUseEyeClinic)
            menu[3] = {
                path: 'reports',
                title: 'Reports',
                component: () => <Reports reports={ reports } department="optical" />
            }

        // Outreach menu entry (Phase 2). Visible to anyone with outreach-screening or
        // outreach-manage. Renders the mobile-first Outreach component.
        if (canScreenOutreach)
            menu[4] = {
                path: 'outreach',
                title: 'Outreach',
                component: Outreach,
            }

        // Only redirect to the first *visible* menu item, so a CHW (no eye-clinic permission,
        // and no Outreach items yet in Phase 1) isn't pushed into a page they can't use.
        const visible = menu.filter(item => item)
        if (visible[0]) {
            const target = `/app/optical/${visible[0].path}`
            if (!pathname.current.startsWith(target)) history.push(target)
        }
    }, [user, reports, canUseEyeClinic, canScreenOutreach])

    // Filter the menu for rendering: hide clinical/dispensing/inventory/reports from users
    // without eye-clinic permission. (Consultation + Dispensing are indices 0 and 1;
    // Inventory 2, Reports 3 are only set when canUseEyeClinic is true; Outreach 4 is set
    // when canScreenOutreach is true.)
    const visibleMenu = menu.filter((item, index) => {
        if (!item) return false
        if (index < 2) return canUseEyeClinic
        if (index === 4) return canScreenOutreach
        return true
    })

    // A CHW (outreach-screening only, no eye-clinic) gets a full-screen mobile layout
    // with no desktop sidebar - the outreach pages are phone-first (§3.2). Eye Clinic
    // Staff keep the normal desktop menu with an Outreach entry added. Placed after all
    // hooks so it doesn't violate react-hooks/rules-of-hooks.
    if (canScreenOutreach && !canUseEyeClinic) {
        return <Outreach />
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto flex flex-no-wrap max-h-full overflow-hidden">
            <div className="w-1/6 overflow-y-scroll">
                <p className="text-2xl font-semibold">Eye Clinic</p>
                <SideMenu namespace="optical" menu={ visibleMenu } />
            </div>
            <div className="w-5/6 overflow-y-scroll">
                <Routing namespace="optical" menu={ visibleMenu } />
            </div>
        </div>
    )
}

export default Optical
